/**
 * Base de datos local (IndexedDB vía Dexie) — caché persistente in-page y outbox offline.
 *
 * · `cache`   → persistencia de TanStack Query (sobrevive al cierre del navegador).
 * · `outbox`  → mutaciones encoladas cuando no hay red (ADR-0007).
 * · `settings`→ preferencias locales (tema, terminal asignada).
 *
 * El nombre de la base incluye `VITE_CACHE_VERSION`: subirla invalida la caché vieja sin
 * migraciones frágiles.
 */
import Dexie, { type Table } from 'dexie';

export type OutboxStatus = 'pending' | 'sending' | 'done' | 'failed';

export interface OutboxEntry {
  /** La clave de idempotencia es el id: garantiza que el servidor deduplique. */
  id: string;
  path: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body: unknown;
  status: OutboxStatus;
  attempts: number;
  createdAt: number;
  nextAttemptAt: number;
  lastError?: string;
  /** Etiqueta legible para la UI ("Boleto de Tony"). */
  label: string;
}

export interface CacheEntry {
  key: string;
  value: unknown;
  updatedAt: number;
}

export interface SettingEntry {
  key: string;
  value: string;
}

const CACHE_VERSION: string = import.meta.env.VITE_CACHE_VERSION ?? 'v1';

export class PitDatabase extends Dexie {
  outbox!: Table<OutboxEntry, string>;
  cache!: Table<CacheEntry, string>;
  settings!: Table<SettingEntry, string>;

  constructor(name = `thepit-${CACHE_VERSION}`) {
    super(name);
    this.version(1).stores({
      outbox: 'id, status, createdAt, nextAttemptAt',
      cache: 'key, updatedAt',
      settings: 'key',
    });
  }
}

export const db = new PitDatabase();

export async function getSetting(key: string): Promise<string | null> {
  const row = await db.settings.get(key);
  return row?.value ?? null;
}

export async function setSetting(key: string, value: string): Promise<void> {
  await db.settings.put({ key, value });
}

/** Borra caché y outbox (usado por "limpiar datos locales" en Ajustes). */
export async function resetLocalData(): Promise<void> {
  await db.delete();
  await db.open();
}
